import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { Plus, Trash2, MessageCircle, Phone, Search, X } from 'lucide-react';
import { API, getImageUrl, whatsappTemplates, openWhatsApp } from './adminUtils';

const PlayersTab = ({ ctx }) => {
  const {
    players,
    newPlayerName, setNewPlayerName,
    teamPhotoUrl, setTeamPhotoUrl, uploadingTeamPhoto, handleUploadTeamPhoto,
    showImportPhones, setShowImportPhones, importCsvData, setImportCsvData,
    importingPhones, importResult, setImportResult, handleImportPhones,
    handleAddPlayer, handleToggleTitular, handleDeletePlayer,
    editingName, setEditingName, newName, setNewName, handleUpdatePlayerName,
    editingPassword, setEditingPassword, newPassword, setNewPassword, handleUpdatePlayerPassword,
    editingPhoto, setEditingPhoto, uploadingPhoto, handleUpdatePhoto,
    editingHighlightPhoto, setEditingHighlightPhoto, uploadingHighlightPhoto, handleUpdateHighlightPhoto,
    editingPhone, setEditingPhone, newPhone, setNewPhone, handleUpdatePlayerPhone,
    editingCardPhoto, setEditingCardPhoto, uploadingCardPhoto,
    handleUpdateCardPhoto, handleRemoveCardPhoto, handleUpdateNationality,
  } = ctx;

  // Filters / sorting for the main players list
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMode, setFilterMode] = useState('all'); // all | titulares | active | inactive
  const [sortMode, setSortMode] = useState('name-asc'); // name-asc | name-desc | games-desc | games-asc | goals-desc | ranking-desc

  const filteredPlayers = useMemo(() => {
    const s = searchTerm.trim().toLowerCase();
    let list = players.filter(p => (p.name || '').toLowerCase().includes(s));
    if (filterMode === 'titulares') list = list.filter(p => p.is_titular);
    else if (filterMode === 'active') list = list.filter(p => (p.games_played || p.all_time_games_played || 0) > 0);
    else if (filterMode === 'inactive') list = list.filter(p => (p.games_played || 0) === 0 && (p.all_time_games_played || 0) === 0);
    const g = p => (p.games_played || p.all_time_games_played || 0);
    switch (sortMode) {
      case 'name-desc': list = [...list].sort((a, b) => b.name.localeCompare(a.name, 'pt')); break;
      case 'games-desc': list = [...list].sort((a, b) => g(b) - g(a) || a.name.localeCompare(b.name, 'pt')); break;
      case 'games-asc': list = [...list].sort((a, b) => g(a) - g(b) || a.name.localeCompare(b.name, 'pt')); break;
      case 'goals-desc': list = [...list].sort((a, b) => (b.goals || 0) - (a.goals || 0) || a.name.localeCompare(b.name, 'pt')); break;
      case 'ranking-desc': list = [...list].sort((a, b) => (b.ranking_avg || 0) - (a.ranking_avg || 0) || a.name.localeCompare(b.name, 'pt')); break;
      case 'name-asc':
      default: list = [...list].sort((a, b) => a.name.localeCompare(b.name, 'pt'));
    }
    return list;
  }, [players, searchTerm, filterMode, sortMode]);

  const NATIONALITIES = [
    ['', '— Sem nacionalidade —'],
    ['PT', '🇵🇹 Portugal'], ['BR', '🇧🇷 Brasil'], ['CV', '🇨🇻 Cabo Verde'],
    ['AO', '🇦🇴 Angola'], ['GW', '🇬🇼 Guiné-Bissau'], ['MZ', '🇲🇿 Moçambique'],
    ['ES', '🇪🇸 Espanha'], ['FR', '🇫🇷 França'], ['GB', '🇬🇧 Reino Unido'],
    ['DE', '🇩🇪 Alemanha'], ['IT', '🇮🇹 Itália'], ['NL', '🇳🇱 Holanda'],
    ['AR', '🇦🇷 Argentina'], ['UY', '🇺🇾 Uruguai'], ['CL', '🇨🇱 Chile'],
    ['CO', '🇨🇴 Colômbia'], ['VE', '🇻🇪 Venezuela'], ['MX', '🇲🇽 México'],
    ['US', '🇺🇸 EUA'],
  ];

  return (
    <div className="card">
      <h2 className="text-3xl font-bold mb-6">GERENCIAR JOGADORES</h2>

      {/* Team Photo Section */}
      <div className="mb-8 p-6 border-2 border-purple-500 bg-purple-50">
        <h3 className="text-xl font-bold text-purple-700 mb-4 flex items-center gap-2">
          📸 FOTO DO TIME
        </h3>
        <p className="text-base text-gray-700 mb-4">
          Esta foto aparece no <strong>carrossel da página inicial</strong> como último slide.
        </p>

        {teamPhotoUrl && (
          <div className="mb-4">
            <p className="text-sm font-bold mb-2">Foto atual:</p>
            <img
              src={getImageUrl(teamPhotoUrl)}
              alt="Foto do Time"
              className="w-full max-w-md h-48 object-cover border-2 border-purple-300 rounded-lg"
            />
          </div>
        )}

        <div className="flex items-center gap-4">
          <label className="btn-primary bg-purple-600 hover:bg-purple-700 cursor-pointer">
            {uploadingTeamPhoto ? 'Enviando...' : '📤 ENVIAR NOVA FOTO'}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) handleUploadTeamPhoto(file);
              }}
              className="hidden"
              disabled={uploadingTeamPhoto}
              data-testid="team-photo-input"
            />
          </label>
          {teamPhotoUrl && (
            <button
              onClick={async () => {
                if (window.confirm('Remover a foto do time?')) {
                  await axios.put(`${API}/admin/settings/team-photo`, { team_photo_url: '' });
                  setTeamPhotoUrl('');
                }
              }}
              className="btn-secondary text-red-600 border-red-500"
            >
              🗑️ Remover
            </button>
          )}
        </div>

        <p className="text-xs text-gray-500 mt-3">
          💡 Recomendado: Imagem em formato paisagem (16:9), mínimo 800x450px
        </p>
      </div>

      {/* Titulares Section */}
      <div className="mb-8 p-6 border-2 border-yellow-500 bg-yellow-50">
        <h3 className="text-xl font-bold text-yellow-700 mb-4 flex items-center gap-2">
          ⭐ TITULARES (Mensalistas)
        </h3>
        <p className="text-base text-gray-700 mb-4">
          Jogadores marcados como titulares são <strong>automaticamente confirmados</strong> ao criar nova partida.
        </p>
        <div className="flex flex-wrap gap-2">
          {players.filter(p => p.is_titular).length === 0 ? (
            <p className="text-gray-500 italic">Nenhum titular definido</p>
          ) : (
            players.filter(p => p.is_titular).map(p => (
              <span key={p.name} className="px-3 py-1 bg-yellow-200 border border-yellow-600 font-bold text-sm rounded">
                ⭐ {p.name}
              </span>
            ))
          )}
        </div>
      </div>

      {/* Import Phones Section */}
      <div className="mb-8 p-6 border-2 border-green-500 bg-green-50">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-green-700 flex items-center gap-2">
            📱 IMPORTAR TELEFONES EM MASSA
          </h3>
          <button
            onClick={() => setShowImportPhones(!showImportPhones)}
            className="btn-secondary text-sm py-2 px-4"
            data-testid="toggle-import-phones"
          >
            {showImportPhones ? '✕ Fechar' : '+ Abrir'}
          </button>
        </div>

        {showImportPhones && (
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Cole os dados no formato <strong>nome,telefone</strong> (um por linha). Exemplo:
            </p>
            <div className="bg-white p-3 border border-gray-300 text-sm font-mono">
              <p>Vini,912345678</p>
              <p>Rafael cruzeirense,923456789</p>
              <p>Eduardo,934567890</p>
            </div>

            <textarea
              value={importCsvData}
              onChange={(e) => setImportCsvData(e.target.value)}
              placeholder="Cole aqui os dados: nome,telefone (um por linha)"
              className="input-field w-full h-40 font-mono text-sm"
              data-testid="import-phones-textarea"
            />

            <div className="flex gap-4">
              <button
                onClick={handleImportPhones}
                disabled={importingPhones}
                className="btn-primary bg-green-600 hover:bg-green-700"
                data-testid="import-phones-btn"
              >
                {importingPhones ? 'Importando...' : '📥 IMPORTAR TELEFONES'}
              </button>
              <button
                onClick={() => {
                  setImportCsvData('');
                  setImportResult(null);
                }}
                className="btn-secondary"
              >
                Limpar
              </button>
            </div>

            {importResult && (
              <div className="mt-4 p-4 border-2 border-gray-300 bg-white">
                <h4 className="font-bold mb-2">Resultado da Importação:</h4>
                <div className="grid grid-cols-3 gap-4 text-center mb-4">
                  <div className="p-3 bg-green-100 border border-green-500 rounded">
                    <p className="text-2xl font-bold text-green-600">{importResult.updated_count}</p>
                    <p className="text-xs text-green-700">Atualizados</p>
                  </div>
                  <div className="p-3 bg-yellow-100 border border-yellow-500 rounded">
                    <p className="text-2xl font-bold text-yellow-600">{importResult.not_found_count}</p>
                    <p className="text-xs text-yellow-700">Não Encontrados</p>
                  </div>
                  <div className="p-3 bg-red-100 border border-red-500 rounded">
                    <p className="text-2xl font-bold text-red-600">{importResult.error_count}</p>
                    <p className="text-xs text-red-700">Erros</p>
                  </div>
                </div>

                {importResult.details.updated.length > 0 && (
                  <div className="mb-2">
                    <p className="text-sm font-bold text-green-700">✅ Atualizados:</p>
                    <p className="text-sm text-gray-600">
                      {importResult.details.updated.map(p => `${p.name} (${p.phone})`).join(', ')}
                    </p>
                  </div>
                )}

                {importResult.details.not_found.length > 0 && (
                  <div className="mb-2">
                    <p className="text-sm font-bold text-yellow-700">⚠️ Não encontrados:</p>
                    <p className="text-sm text-gray-600">
                      {importResult.details.not_found.join(', ')}
                    </p>
                  </div>
                )}

                {importResult.details.errors.length > 0 && (
                  <div>
                    <p className="text-sm font-bold text-red-700">❌ Erros:</p>
                    <p className="text-sm text-gray-600">
                      {importResult.details.errors.join(', ')}
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="p-3 bg-blue-50 border border-blue-300 text-sm">
              <p>
                📊 <strong>Estatísticas:</strong> {players.filter(p => p.phone).length} jogadores com telefone, {players.filter(p => !p.phone).length} sem telefone
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Avisar Votação Section */}
      <div className="mb-8 p-6 border-2 border-blue-500 bg-blue-50">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <h3 className="text-xl font-bold text-blue-700 flex items-center gap-2">
            🗳️ AVISAR SOBRE VOTAÇÃO
          </h3>
          {(() => {
            const playersWithPhone = players.filter(p => p.phone);
            if (playersWithPhone.length > 1) {
              return (
                <button
                  onClick={() => {
                    if (window.confirm(`Isto vai abrir ${playersWithPhone.length} janelas do WhatsApp para avisar sobre votação. Continuar?`)) {
                      playersWithPhone.forEach((player, index) => {
                        setTimeout(() => {
                          openWhatsApp(player.phone, whatsappTemplates.votacao(player.name));
                        }, index * 800);
                      });
                    }
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-4 py-2 rounded flex items-center gap-2"
                  data-testid="whatsapp-votacao-all"
                >
                  <MessageCircle size={18} /> AVISAR TODOS ({playersWithPhone.length})
                </button>
              );
            }
            return null;
          })()}
        </div>
        <p className="text-sm text-gray-700 mb-3">
          Envie uma mensagem para avisar que a votação está aberta:
        </p>
        <div className="flex flex-wrap gap-2">
          {players.filter(p => p.phone).map(player => (
            <button
              key={player.name}
              onClick={() => openWhatsApp(player.phone, whatsappTemplates.votacao(player.name))}
              className="bg-blue-500 hover:bg-blue-600 text-white text-sm px-3 py-1 rounded flex items-center gap-1"
              data-testid={`whatsapp-votacao-${player.name}`}
            >
              <MessageCircle size={12} /> {player.name}
            </button>
          ))}
        </div>
        {players.filter(p => p.phone).length === 0 && (
          <p className="text-gray-500 italic">Nenhum jogador com telefone cadastrado</p>
        )}
      </div>

      {/* Add Player Form */}
      <form onSubmit={handleAddPlayer} className="mb-8">
        <div className="flex gap-4">
          <input
            type="text"
            className="input-field flex-1"
            value={newPlayerName}
            onChange={(e) => setNewPlayerName(e.target.value)}
            placeholder="Nome do novo jogador"
            data-testid="new-player-input"
          />
          <button type="submit" className="btn-primary" data-testid="add-player-btn">
            <Plus size={24} strokeWidth={3} />
          </button>
        </div>
      </form>

      {/* Filters toolbar */}
      <div className="mb-4 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center flex-wrap p-3 bg-zinc-50 border-2 border-zinc-200 rounded-lg" data-testid="players-filters">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Procurar jogador…"
            className="w-full pl-9 pr-9 py-2 border-2 border-black rounded font-medium"
            data-testid="players-search"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-black" data-testid="clear-search">
              <X size={14} strokeWidth={3} />
            </button>
          )}
        </div>
        <select
          value={filterMode}
          onChange={(e) => setFilterMode(e.target.value)}
          className="border-2 border-black rounded px-2 py-2 font-bold text-sm bg-white"
          data-testid="players-filter-mode"
        >
          <option value="all">👥 Todos</option>
          <option value="titulares">⭐ Só titulares</option>
          <option value="active">🏃 Ativos (≥1 jogo)</option>
          <option value="inactive">💤 Inativos (0 jogos)</option>
        </select>
        <select
          value={sortMode}
          onChange={(e) => setSortMode(e.target.value)}
          className="border-2 border-black rounded px-2 py-2 font-bold text-sm bg-white"
          data-testid="players-sort-mode"
        >
          <option value="name-asc">🔤 A–Z</option>
          <option value="name-desc">🔤 Z–A</option>
          <option value="games-desc">🕐 + Jogos</option>
          <option value="games-asc">🕐 – Jogos</option>
          <option value="goals-desc">⚽ + Golos</option>
          <option value="ranking-desc">⭐ Melhor ranking</option>
        </select>
        <span className="text-xs text-zinc-500 font-bold sm:ml-1" data-testid="players-filter-count">
          {filteredPlayers.length}/{players.length}
        </span>
      </div>

      {/* Player List */}
      <div className="space-y-4">
        {filteredPlayers.length === 0 ? (
          <p className="text-center text-zinc-500 italic py-6">Nenhum jogador corresponde aos filtros.</p>
        ) : filteredPlayers.map((player) => (
          <div
            key={player.name}
            className="border-2 border-black p-4"
            style={{
              backgroundColor: player.is_titular ? '#FEF9C3' : 'white',
              borderColor: player.is_titular ? '#CA8A04' : 'black'
            }}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-4 flex-1">
                <div className="flex-shrink-0">
                  {player.profile_image_url ? (
                    <img
                      src={getImageUrl(player.profile_image_url)}
                      alt={player.name}
                      className="w-16 h-16 object-cover border-2 border-black"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                      }}
                    />
                  ) : null}
                  <div
                    className="w-16 h-16 bg-gray-200 border-2 border-black flex items-center justify-center"
                    style={{ display: player.profile_image_url ? 'none' : 'flex' }}
                  >
                    <span className="text-2xl font-bold">{player.name.charAt(0)}</span>
                  </div>
                </div>

                <div className="flex-1 min-w-0 overflow-hidden">
                  <p className="text-xl font-bold">
                    {player.is_titular && '⭐ '}{player.name}
                  </p>
                  <p className="text-base text-gray-600">
                    ⭐ {(player.ranking_avg ?? 0).toFixed(2)} | ⚽ {player.goals ?? 0}G | 🎯 {player.assists ?? 0}A
                  </p>

                  <button
                    onClick={() => handleToggleTitular(player.name, !player.is_titular)}
                    className={`mt-2 px-3 py-1 text-sm font-bold border-2 ${
                      player.is_titular
                        ? 'bg-yellow-200 border-yellow-600 text-yellow-800'
                        : 'bg-gray-100 border-gray-400 text-gray-600 hover:bg-yellow-100'
                    }`}
                    data-testid={`titular-toggle-${player.name}`}
                  >
                    {player.is_titular ? '⭐ TITULAR' : '☆ Marcar como Titular'}
                  </button>

                  {/* Name Edit Section */}
                  {editingName === player.name ? (
                    <div className="mt-2 flex gap-2">
                      <input
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder="Novo nome"
                        className="input-field text-sm py-2 flex-1"
                        data-testid={`name-input-${player.name}`}
                        autoFocus
                      />
                      <button
                        onClick={() => handleUpdatePlayerName(player.name)}
                        className="btn-primary text-sm py-2 px-4"
                        data-testid={`save-name-${player.name}`}
                      >
                        Salvar
                      </button>
                      <button
                        onClick={() => {
                          setEditingName(null);
                          setNewName('');
                        }}
                        className="btn-secondary text-sm py-2 px-4"
                      >
                        Cancelar
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setNewName(player.name);
                        setEditingName(player.name);
                      }}
                      className="text-sm underline mt-1"
                      data-testid={`edit-name-${player.name}`}
                    >
                      Editar nome
                    </button>
                  )}

                  {/* Password Edit Section */}
                  {editingPassword === player.name ? (
                    <div className="mt-2 flex gap-2">
                      <input
                        type="text"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Nova senha (4-6 dígitos)"
                        className="input-field text-sm py-2 flex-1"
                        maxLength={6}
                        data-testid={`password-input-${player.name}`}
                      />
                      <button
                        onClick={() => handleUpdatePlayerPassword(player.name)}
                        className="btn-primary text-sm py-2 px-4"
                        data-testid={`save-password-${player.name}`}
                      >
                        Salvar
                      </button>
                      <button
                        onClick={() => {
                          setEditingPassword(null);
                          setNewPassword('');
                        }}
                        className="btn-secondary text-sm py-2 px-4"
                      >
                        Cancelar
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setEditingPassword(player.name);
                        setNewPassword('');
                      }}
                      className="text-sm underline mt-1"
                      data-testid={`edit-password-${player.name}`}
                    >
                      Definir senha
                    </button>
                  )}

                  {/* Photo Edit Section */}
                  {editingPhoto === player.name ? (
                    <div className="mt-2 flex gap-2 items-center">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            handleUpdatePhoto(player.name, file);
                          }
                        }}
                        className="text-sm"
                        data-testid={`photo-file-input-${player.name}`}
                      />
                      <button
                        onClick={() => {
                          setEditingPhoto(null);
                        }}
                        className="btn-secondary text-sm py-2 px-4"
                      >
                        Cancelar
                      </button>
                      {uploadingPhoto === player.name && (
                        <span className="text-sm font-bold text-orange-600">Enviando...</span>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setEditingPhoto(player.name);
                      }}
                      className="text-sm underline mt-1"
                      data-testid={`edit-photo-${player.name}`}
                      disabled={uploadingPhoto === player.name}
                    >
                      {player.profile_image_url ? '📷 Alterar foto perfil' : '📷 Adicionar foto perfil'}
                    </button>
                  )}

                  {/* Highlight Photo Edit Section */}
                  {editingHighlightPhoto === player.name ? (
                    <div className="mt-2 flex gap-2 items-center">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            handleUpdateHighlightPhoto(player.name, file);
                          }
                        }}
                        className="text-sm"
                        data-testid={`highlight-photo-file-input-${player.name}`}
                      />
                      <button
                        onClick={() => {
                          setEditingHighlightPhoto(null);
                        }}
                        className="btn-secondary text-sm py-2 px-4"
                      >
                        Cancelar
                      </button>
                      {uploadingHighlightPhoto === player.name && (
                        <span className="text-sm font-bold text-yellow-600">Enviando...</span>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setEditingHighlightPhoto(player.name);
                      }}
                      className={`text-sm underline mt-1 ${player.highlight_image_url ? 'text-yellow-600' : ''}`}
                      data-testid={`edit-highlight-photo-${player.name}`}
                      disabled={uploadingHighlightPhoto === player.name}
                    >
                      {player.highlight_image_url ? '🌟 Alterar foto destaque' : '🌟 Adicionar foto destaque'}
                    </button>
                  )}

                  {/* === Card Photo (FIFA-style) === */}
                  {editingCardPhoto === player.name ? (
                    <div className="mt-2 flex flex-wrap gap-2 items-center">
                      <input
                        type="file"
                        accept="image/png,image/webp,image/jpeg"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) handleUpdateCardPhoto(player.name, file);
                        }}
                        className="text-sm"
                        data-testid={`card-photo-file-input-${player.name}`}
                      />
                      <button
                        onClick={() => setEditingCardPhoto(null)}
                        className="btn-secondary text-sm py-2 px-4"
                      >
                        Cancelar
                      </button>
                      {uploadingCardPhoto === player.name && (
                        <span className="text-sm font-bold text-blue-600">A enviar…</span>
                      )}
                    </div>
                  ) : (
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => setEditingCardPhoto(player.name)}
                        className={`text-sm underline ${player.card_image_url ? 'text-blue-700' : ''}`}
                        data-testid={`edit-card-photo-${player.name}`}
                        disabled={uploadingCardPhoto === player.name}
                      >
                        {player.card_image_url ? '🃏 Alterar foto da carta' : '🃏 Adicionar foto da carta'}
                      </button>
                      {player.card_image_url && (
                        <button
                          onClick={() => handleRemoveCardPhoto(player.name)}
                          className="text-xs text-red-600 underline"
                          data-testid={`remove-card-photo-${player.name}`}
                        >
                          remover
                        </button>
                      )}
                      <span className="text-[10px] text-zinc-400">PNG transparente · 600×800 · ≤ 3 MB</span>
                    </div>
                  )}

                  {/* === Nationality === */}
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-sm font-bold text-zinc-600">Nacionalidade:</span>
                    <select
                      value={player.nationality || ''}
                      onChange={(e) => handleUpdateNationality(player.name, e.target.value)}
                      className="text-sm border-2 border-black px-2 py-1 bg-white font-medium"
                      data-testid={`nationality-select-${player.name}`}
                    >
                      {NATIONALITIES.map(([code, label]) => (
                        <option key={code} value={code}>{label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Phone Edit Section */}
                  {editingPhone === player.name ? (
                    <div className="mt-2 flex gap-2">
                      <input
                        type="tel"
                        value={newPhone}
                        onChange={(e) => setNewPhone(e.target.value)}
                        placeholder="Ex: 912345678"
                        className="input-field text-sm py-2 flex-1"
                        data-testid={`phone-input-${player.name}`}
                      />
                      <button
                        onClick={() => handleUpdatePlayerPhone(player.name)}
                        className="btn-primary text-sm py-2 px-4"
                        data-testid={`save-phone-${player.name}`}
                      >
                        Salvar
                      </button>
                      <button
                        onClick={() => {
                          setEditingPhone(null);
                          setNewPhone('');
                        }}
                        className="btn-secondary text-sm py-2 px-4"
                      >
                        Cancelar
                      </button>
                    </div>
                  ) : (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => {
                          setEditingPhone(player.name);
                          setNewPhone(player.phone || '');
                        }}
                        className="text-sm underline flex items-center gap-1"
                        data-testid={`edit-phone-${player.name}`}
                      >
                        <Phone size={14} />
                        {player.phone ? `📱 ${player.phone}` : 'Adicionar telefone'}
                      </button>

                      {player.phone && (
                        <div className="flex gap-1 flex-wrap">
                          <button
                            onClick={() => openWhatsApp(player.phone, whatsappTemplates.convite(player.name, '[DATA]', '[HORA]', '[LOCAL]'))}
                            className="bg-green-500 hover:bg-green-600 text-white text-xs px-2 py-1 rounded flex items-center gap-1"
                            title="Convidar para partida"
                            data-testid={`whatsapp-invite-${player.name}`}
                          >
                            <MessageCircle size={12} /> Convidar
                          </button>
                          <button
                            onClick={() => openWhatsApp(player.phone, whatsappTemplates.titular(player.name))}
                            className="bg-yellow-500 hover:bg-yellow-600 text-white text-xs px-2 py-1 rounded flex items-center gap-1"
                            title="Perguntar sobre titular"
                            data-testid={`whatsapp-titular-${player.name}`}
                          >
                            <MessageCircle size={12} /> Titular?
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <button
                onClick={() => handleDeletePlayer(player.name)}
                className="text-red-600 hover:text-red-800 flex-shrink-0"
                data-testid={`delete-player-${player.name}`}
              >
                <Trash2 size={24} strokeWidth={2.5} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PlayersTab;
